import React from "react";
import moment from "moment";
import { Modal } from "../components";
import TrainJourneyDetailScreen from "./screens/TrainJourneyDetailScreen";
import TrainJourneyEditScreen from "./screens/TrainJourneyEditScreen";
import ExistingTrainSegmentEditScreen from "./screens/ExistingTrainSegmentEditScreen";
import NewTrainJourneyEditScreen from "./screens/NewTrainJourneyEditScreen";
import { emptyTrain, trainSegmentNewCopy } from "./utils";
import { ButtonSecondary } from "components/Atoms/Button";

interface Props {
  train?: server.dto.LegacyTrain
}

interface State {
  isModalVisible: boolean
  page: Pages
  train: server.dto.LegacyTrain
  onNewSegmentClick: () => void
  editingSegment: server.dto.LegacyTrainSegment
  refreshOnClose: boolean
  fromItem: server.dto.FromItem
  initialDate: Date
}

type Pages = "detail" | "editJourneyDetail" | "editSegment" | "createTrainJourney";

export default class TrainModal extends React.Component<Props, State> {
  state: State = {
    isModalVisible: false,
    page: "detail",
    train: this.props.train,
    onNewSegmentClick: undefined,
    editingSegment: undefined,
    refreshOnClose: false,
    fromItem: undefined,
    initialDate: new Date()
  };

  componentDidMount() {
    this.export();
  }

  closeModal = () => {
    if (this.state.refreshOnClose) {
      window.location.reload();
    } else {
      this.setState({ isModalVisible: false });
    }
  };

  onTrainDetailEdit = () => {
    this.setState({ page: "editJourneyDetail" });
  };

  backToDetail = (newTrain: server.dto.LegacyTrain) => {
    this.setState({
      page: "detail",
      train: newTrain,
      refreshOnClose: true
    });
  };

  setOnNewSegmentClick = (callback: () => void) => {
    this.setState({ onNewSegmentClick: callback });
  };

  isAddSegmentVisible = () => this.state.isModalVisible && (["detail", "createTrainJourney"] as Pages[]).includes(this.state.page);

  onSegmentEditClicked = (segment: server.dto.LegacyTrainSegment) => {
    this.setState({
      editingSegment: segment,
      page: "editSegment"
    });
  };

  getSegmentHandler = () => {
    if (this.state.page === "createTrainJourney") {
      return this.state.onNewSegmentClick;
    }

    // Get the final train station
    const latestTrainSegment = this.state.train.segments.reduce((acc, curr) => {
      if (acc) {
        const endMoment = moment.utc(curr.end.dateTimeLocal);
        return endMoment.isAfter(moment.utc(acc.end.dateTimeLocal)) ? curr : acc;
      } else {
        return curr;
      }
    }, undefined);
    const minimalSegment = trainSegmentNewCopy(latestTrainSegment?.end.dateTimeLocal, latestTrainSegment?.end.station);

    return () => {
      this.setState({
        page: "editSegment",
        editingSegment: minimalSegment
      });
    };
  };

  onTrainUpdated = (newTrain: server.dto.LegacyTrain) => {
    this.setState({ train: newTrain });
  };

  onSegmentDeleted = async (segment: server.dto.LegacyTrainSegment) => {
    try {
      const result = await window.Api.Trains.DeleteSegment(segment.id);
      this.setState({ train: result.data, refreshOnClose: true });
    } catch {
      // Could happen if the user deletes an already deleted item, so just ignore
    }
  };

  openModalAndEditSegment = async (segmentId: number) => {
    const trainResult = await window.Api.Trains.GetTrainBySegment(segmentId);
    this.setState({
      train: trainResult.data,
      editingSegment: trainResult.data.segments.find(seg => seg.id === segmentId),
      isModalVisible: true,
      page: "editSegment"
    });
  };

  openModalAndViewDetail = async (trainId: number) => {
    const trainResult = await window.Api.Trains.GetTrain(trainId);
    this.setState({
      train: trainResult.data,
      editingSegment: undefined,
      isModalVisible: true,
      page: "detail"
    });
  };

  openModalAndEditDetails = async (trainId: number) => {
    const trainResult = await window.Api.Trains.GetTrain(trainId);
    this.setState({
      train: trainResult.data,
      editingSegment: undefined,
      isModalVisible: true,
      page: "editJourneyDetail"
    });
  };

  openModalAndCreateTrainJourney = async (lookupDate: Date, artist: server.dto.ArtistSmall, fromItem: server.dto.FromItem) => {
    const train = emptyTrain();
    train.artist = artist;
    if (train.artist?.artistId) {
      const instruction: server.dto.ListContactByCompanyInstruction = {
        companyId: artist.artistId,
        page: 1,
        perPage: 1000,
        isActive: true,
        defaultSelect: true
      };

      const contacts = await window.CoreApi.CompanyToContact.ListContactByCompany(instruction);

      const mapped = contacts
        .data
        .data
        .map(item => (item.contact));

      train.travellers = mapped;
    }

    this.setState({
      train,
      editingSegment: undefined,
      isModalVisible: true,
      page: "createTrainJourney",
      fromItem,
      initialDate: lookupDate || new Date()
    });
  };

  openModalAndCreateSegment = async (trainId: number) => {
    const trainResult = await window.Api.Trains.GetTrain(trainId);
    this.setState({
      train: trainResult.data,
      editingSegment: undefined,
      isModalVisible: true,
      page: "detail"
    }, () => {
      this.getSegmentHandler()();
    });
  };

  public render(): JSX.Element {
    const { page } = this.state;

    return <Modal
      width="large"
      title={RESX.Train.resxTrain}
      isOpen={this.state.isModalVisible}
      handleClose={this.closeModal}
      height={750}
      hasAutoScrollBox
      headerButton={this.isAddSegmentVisible() && <ButtonSecondary onClick={this.getSegmentHandler()} label={RESX.Flight.AddSegment} />}
    >
      {page === "detail" && <div className="grow1 scrollbox-auto"><TrainJourneyDetailScreen onTrainUpdated={this.onTrainUpdated} onSegmentDeleted={this.onSegmentDeleted} onSegmentEditClicked={this.onSegmentEditClicked} train={this.state.train} onTrainDetailEditClicked={this.onTrainDetailEdit} /></div>}
      {page === "editJourneyDetail" && <TrainJourneyEditScreen fromItem={this.state.fromItem} train={this.state.train} onFinished={this.backToDetail} />}
      {page === "editSegment" && <ExistingTrainSegmentEditScreen segmentToEdit={this.state.editingSegment} train={this.state.train} onFinished={this.backToDetail} />}
      {page === "createTrainJourney" && <NewTrainJourneyEditScreen initialDate={this.state.initialDate} fromItem={this.state.fromItem} initialTrain={this.state.train} onFinished={this.backToDetail} setOnSegmentAddedHandler={this.setOnNewSegmentClick} />}
    </Modal>;
  }

  export = () => {
    SystemOneLibrary.exports = {
      ...SystemOneLibrary.exports,
      openModalAndEditDetails: this.openModalAndEditDetails,
      openModalAndViewDetail: this.openModalAndViewDetail,
      openModalAndEditSegment: this.openModalAndEditSegment,
      openModalAndCreateTrainJourney: this.openModalAndCreateTrainJourney,
      openModalAndCreateSegment: this.openModalAndCreateSegment
    };
  };
}